import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import React, { useEffect, useMemo } from "react";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { useFormContext, useWatch } from "react-hook-form";
import {
  useEquipmentMide,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import { InputAdornment } from "@mui/material";
import { OPTIONS_YES_NO } from "./utils/constants";

const PreparationBRBT = () => {
  const { currentPreparation } = useAnalysis();
  const { watch, getValues, setValue } = useFormContext();
  const { userList } = useUser();
  const filteredProcedures = useFilteredProcedures("PRE");
  const { equipmentMideListFiltered, loading, fetchByAnalysisType } =
    useEquipmentMide();
  const { typesUnitsis } = useTypesUnits();
  const watchAnalysis = watch("analysis");

  useEffect(() => {
    const fetchData = async () => {
      await fetchByAnalysisType(watchAnalysis?.id);
    };
    if (watchAnalysis) fetchData();
  }, [watchAnalysis]);

  const validPreparation = watch("valid_preparation");
  const stylesInput = useMemo(
    () => ({
      backgroundColor:
        validPreparation === "N"
          ? "#ef233c"
          : validPreparation === "S"
          ? "#a7c957"
          : "#fff",
    }),
    [validPreparation]
  );

  const pesadas = useWatch({
    name: [
      "planchette_with_precipitate_5",
      "planchette_with_precipitate_4",
      "planchette_with_precipitate_3",
      "planchette_with_precipitate_2",
      "planchette_with_precipitate_1",
    ],
  }).filter(Boolean);

  const lastTwoPesadas = useMemo(() => pesadas.slice(0, 2), [pesadas]);
  const constantWeight = watch("constant_weight");
  const mgTorta = watch("mg_torta");
  const planchetteWithPrecipitate =
    parseFloat(watch("planchette_with_precipitate")) || 0;

  const planchette =
    parseFloat(watch("planchette")) || 0;

  useEffect(() => {
    if (lastTwoPesadas.length < 2) return;
    const difference = Math.abs(lastTwoPesadas[0] - lastTwoPesadas[1]) * 1000;
    const selectedOption = OPTIONS_YES_NO.find(
      (opt) => opt.value === (difference <= 0.5 ? 1 : 0)
    );
    setValue("constant_weight", selectedOption);
  }, [lastTwoPesadas]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    /*   if (constantWeight?.value === 1) { */
    if (lastTwoPesadas.length < 2) return;

    const difference = Math.round((lastTwoPesadas[0] - planchette) * 1000);
    setValue("planchette_with_precipitate", difference);
    /*     } else {
      setValue("planchette_with_precipitate", "");
    } */
  }, [constantWeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newValue = isNaN(mgTorta)
      ? undefined
      : mgTorta >= planchetteWithPrecipitate
      ? "S"
      : "N";
    if (newValue !== validPreparation) {
      setValue("valid_preparation", newValue);
    }
  }, [mgTorta, planchetteWithPrecipitate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {currentPreparation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="analyst"
              options={userList}
              label="Analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentPreparation.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("preparation_procedure"))}
              name="preparation_procedure"
              options={filteredProcedures}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              /* validation={{ required: "El procedimiento es requerido" }} Agregar cuando se conecte a base de datos*/
              disabled={!currentPreparation.canEdit}
            />
            <MDatePicker
              name="start_date"
              pickerType="date"
              label="Fecha de inicio"
              validation={{
                required: "La fecha es requerida",
              }}
              disabled={!currentPreparation.canEdit}
            />
            <MTextField
              key={"analysis_amount_sample"}
              label="Cantidad de muestra"
              name="analysis_amount_sample"
              type="text"
              fullWidth
              disabled={!currentPreparation.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(watch("analysis_equipment"))}
              name="analysis_equipment"
              options={equipmentMideListFiltered}
              label="Equipos"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              multiple
              loading={loading}
              maxSelections={1}
              disabled={!currentPreparation.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("analysis_unit_types"))}
              name="analysis_unit_types"
              options={typesUnitsis}
              label="Unidades de la muestra"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              validation={{ required: "El tipo de unidad es requerido" }}
              disabled={!currentPreparation.canEdit}
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={6} mt={1}>
            <MTextField
              name="planchette"
              placeholder={"Tara plancheta (g)"}
              label={"Tara plancheta (g)"}
              type="number"
              validation={{ required: "Tara plancheta es requerida" }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{"(g)"}</InputAdornment>
                ),
              }}
              disabled={!currentPreparation.canEdit}
            />
            {Array.from({ length: 5 }, (_, i) => (
              <MTextField
                key={`planchette_with_precipitate_${i + 1}`}
                name={`planchette_with_precipitate_${i + 1}`}
                placeholder={`Plancheta + Precipitado (Pesada ${i + 1})`}
                label={`Plancheta + Precipitado (Pesada ${i + 1})`}
                type="number"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">(g)</InputAdornment>
                  ),
                }}
                disabled={
                  !currentPreparation.canEdit || (i > 0 && !pesadas[i - 1])
                }
              />
            ))}
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={4} mt={1}>
            <MAutocomplete
              key={JSON.stringify(getValues("constant_weight"))}
              name="constant_weight"
              options={OPTIONS_YES_NO}
              label="¿Pesada constante?"
              getOptionLabel={(option) => `${option.label}` || ""}
              disabled
            />
            <MTextField
              name="mg_torta"
              placeholder="Criterio mg de torta"
              label="Criterio mg de torta"
              type="number"
              fullWidth
            />
            <MTextField
              key={"planchette_with_precipitate"}
              name="planchette_with_precipitate"
              placeholder="Peso Precipitado Torta"
              label="Peso Precipitado Torta"
              type="number"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">(mg)</InputAdornment>
                ),
              }}
              disabled
            />
            <MTextField
              key={"valid_preparation"}
              name="valid_preparation"
              placeholder="Preparación Válida"
              label="Preparación Válida"
              value={validPreparation}
              fullWidth
              disabled
              style={stylesInput}
            />
          </ResponsiveFormContainer>
        </>
      )}
    </>
  );
};

export default PreparationBRBT;
